import { useState } from 'react'
import { Send, Loader2, CheckCircle, XCircle, Clock, ImageIcon, X, CalendarClock } from 'lucide-react'
import { useSendTelegramBroadcast, useTelegramBroadcastHistory, useTelegramContactsCount, useTelegramGroups, type TelegramBot } from '@/api/hooks/useTelegram'
import { toast } from 'sonner'
import { BotSelector } from './BotSelector'
import { MediaPickerModal } from './MediaPickerModal'

const statusIcon: Record<string, JSX.Element> = {
  pending: <Clock className="h-3.5 w-3.5 text-yellow-400" />,
  sending: <Loader2 className="h-3.5 w-3.5 text-blue-400 animate-spin" />,
  completed: <CheckCircle className="h-3.5 w-3.5 text-green-400" />,
  failed: <XCircle className="h-3.5 w-3.5 text-red-400" />,
  scheduled: <CalendarClock className="h-3.5 w-3.5 text-purple-400" />,
}

export function BroadcastTab({ botId, bots, onBotChange }: { botId: string | null; bots: TelegramBot[]; onBotChange: (id: string) => void }) {
  const sendBroadcast = useSendTelegramBroadcast(botId || '')
  const { data: history, isLoading: loadingHistory } = useTelegramBroadcastHistory(botId)
  const { data: contactsCount } = useTelegramContactsCount(botId)
  const { data: groups } = useTelegramGroups(botId)

  const [message, setMessage] = useState('')
  const [imageUrl, setImageUrl] = useState('')
  const [buttonText, setButtonText] = useState('')
  const [buttonUrl, setButtonUrl] = useState('')
  const [target, setTarget] = useState<'contacts' | 'group'>('contacts')
  const [targetGroupId, setTargetGroupId] = useState('')
  const [scheduledAt, setScheduledAt] = useState('')
  const [mediaPicker, setMediaPicker] = useState(false)

  const handleSend = async () => {
    if (!message.trim()) { toast.error('Escreva a mensagem'); return }
    if (target === 'group' && !targetGroupId) { toast.error('Selecione o grupo'); return }
    try {
      const payload: any = {
        message: message.trim(),
        image_url: imageUrl.trim() || undefined,
        button_text: buttonText.trim() || undefined,
        button_url: buttonUrl.trim() || undefined,
        target,
        target_group_id: target === 'group' ? targetGroupId : undefined,
      }
      if (scheduledAt) payload.scheduled_at = scheduledAt

      const res = await sendBroadcast.mutateAsync(payload)
      toast.success(scheduledAt
        ? `Broadcast agendado para ${new Date(scheduledAt).toLocaleString('pt-BR')}`
        : `Broadcast iniciado — ${res.total_contacts} contato(s)`)
      setMessage('')
      setImageUrl('')
      setButtonText('')
      setButtonUrl('')
      setScheduledAt('')
    } catch (e: any) { toast.error(e.message) }
  }

  return (
    <div className="space-y-6">
      <BotSelector bots={bots} value={botId} onChange={onBotChange} />

      {!botId ? (
        <p className="text-sm text-muted-foreground">Adicione um bot primeiro na aba Bots</p>
      ) : (
        <>
          {/* Form */}
          <div className="border border-border rounded-lg p-4 space-y-4">
            <div>
              <label className="text-sm text-muted-foreground">Mensagem *</label>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" placeholder="Texto do broadcast (suporta HTML: <b>, <i>, <a>)" />
            </div>

            {/* Image */}
            <div>
              <label className="text-sm text-muted-foreground">Imagem (opcional)</label>
              {imageUrl ? (
                <div className="mt-1 flex items-start gap-3">
                  <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-border shrink-0">
                    <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setImageUrl('')} className="absolute top-1 right-1 p-0.5 bg-black/60 rounded-full hover:bg-black/80">
                      <X className="h-3 w-3 text-white" />
                    </button>
                  </div>
                  <input type="text" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className="flex-1 px-3 py-2 bg-background border border-border rounded-md text-xs text-muted-foreground" />
                </div>
              ) : (
                <div className="mt-1 flex gap-2">
                  <button onClick={() => setMediaPicker(true)} className="flex items-center gap-2 px-3 py-2 border border-border rounded-md text-sm hover:bg-accent transition-colors">
                    <ImageIcon className="h-4 w-4" /> Biblioteca de Mídias
                  </button>
                  <input type="text" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} className="flex-1 px-3 py-2 bg-background border border-border rounded-md text-sm" placeholder="ou cole uma URL direta" />
                </div>
              )}
            </div>

            {/* Button */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-muted-foreground">Texto do Botão</label>
                <input type="text" value={buttonText} onChange={(e) => setButtonText(e.target.value)} className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" placeholder="Saiba mais" />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">URL do Botão</label>
                <input type="text" value={buttonUrl} onChange={(e) => setButtonUrl(e.target.value)} className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm" placeholder="https://..." />
              </div>
            </div>

            {/* Target */}
            <div>
              <label className="text-sm text-muted-foreground">Alvo</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" checked={target === 'contacts'} onChange={() => setTarget('contacts')} className="accent-primary" />
                  Todos os contatos ({contactsCount?.count ?? 0})
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" checked={target === 'group'} onChange={() => setTarget('group')} className="accent-primary" />
                  Grupo específico
                </label>
              </div>
              {target === 'group' && (
                <select value={targetGroupId} onChange={(e) => setTargetGroupId(e.target.value)} className="w-full mt-2 px-3 py-2 bg-background border border-border rounded-md text-sm">
                  <option value="">Selecione um grupo</option>
                  {groups?.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
                </select>
              )}
            </div>

            {/* Schedule */}
            <div>
              <label className="text-sm text-muted-foreground flex items-center gap-2">
                <CalendarClock className="h-3.5 w-3.5" /> Agendar (opcional)
              </label>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md text-sm"
                min={new Date().toISOString().slice(0, 16)}
              />
              {scheduledAt && <p className="text-xs text-purple-400 mt-1">Será enviado em {new Date(scheduledAt).toLocaleString('pt-BR')}</p>}
            </div>

            <div className="flex justify-end">
              <button onClick={handleSend} disabled={sendBroadcast.isPending || !message.trim()} className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm hover:bg-primary/90 disabled:opacity-50">
                {sendBroadcast.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : scheduledAt ? <CalendarClock className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                {scheduledAt ? 'Agendar Broadcast' : 'Enviar Broadcast'}
              </button>
            </div>
          </div>

          {/* History */}
          <div>
            <h3 className="text-sm font-medium mb-3">Histórico</h3>
            {loadingHistory ? (
              <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando...</div>
            ) : !history?.length ? (
              <p className="text-sm text-muted-foreground">Nenhum broadcast enviado ainda</p>
            ) : (
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-accent/50">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">Data</th>
                      <th className="text-left px-4 py-2 font-medium">Mensagem</th>
                      <th className="text-left px-4 py-2 font-medium">Alvo</th>
                      <th className="text-center px-4 py-2 font-medium">Enviados</th>
                      <th className="text-center px-4 py-2 font-medium">Falhas</th>
                      <th className="text-center px-4 py-2 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {history.map((b) => (
                      <tr key={b.id} className="hover:bg-accent/30">
                        <td className="px-4 py-2 text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(b.created_at).toLocaleString('pt-BR')}
                          {b.scheduled_at && <span className="block text-purple-400">Agendado: {new Date(b.scheduled_at).toLocaleString('pt-BR')}</span>}
                        </td>
                        <td className="px-4 py-2 text-xs max-w-[200px] truncate">{b.message}</td>
                        <td className="px-4 py-2 text-xs">{b.target === 'contacts' ? 'Contatos' : 'Grupo'}</td>
                        <td className="px-4 py-2 text-xs text-center text-green-400">{b.sent_count}/{b.total_contacts}</td>
                        <td className="px-4 py-2 text-xs text-center text-red-400">{b.failed_count}</td>
                        <td className="px-4 py-2 text-center">{statusIcon[b.status] || b.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <MediaPickerModal open={mediaPicker} onOpenChange={setMediaPicker} onSelect={(url) => setImageUrl(url)} />
    </div>
  )
}
